import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NAV_LINKS, IMAGES } from "@/data/content";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Smooth-scroll to hash on the homepage; navigate home first if on another route.
  const goToSection = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + hash);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b backdrop-blur-xl bg-obsidian/95 border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <img src={IMAGES.logo} alt="AlphaPulse Fitness App" className="h-20 md:h-28 w-auto mix-blend-screen" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={goToSection(l.href)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-white/70 hover:text-vitality transition-colors uppercase tracking-wider"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/blog"
            data-testid="nav-blog"
            className="text-sm font-medium text-white/70 hover:text-vitality transition-colors uppercase tracking-wider"
          >
            Blog
          </Link>
        </nav>

        <a
          href="#pricing"
          onClick={goToSection("#pricing")}
          data-testid="header-cta"
          className="hidden md:inline-flex items-center bg-vitality text-black font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-xl hover:bg-vitality-dim transition-colors"
        >
          Start For Free
        </a>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden glass border-t border-white/10 px-6 py-6 space-y-4">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={goToSection(l.href)}
              className="block text-white/80 uppercase tracking-wider font-medium"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/blog"
            onClick={() => setOpen(false)}
            className="block text-white/80 uppercase tracking-wider font-medium"
          >
            Blog
          </Link>
          <a
            href="#pricing"
            onClick={goToSection("#pricing")}
            className="block text-center bg-vitality text-black font-bold uppercase tracking-wider px-6 py-3 rounded-xl"
          >
            Start For Free
          </a>
        </div>
      )}
    </header>
  );
};
